import React, { ChangeEvent, FC, useState } from 'react';
import { FormProvider, useFieldArray } from 'react-hook-form';

import { Button } from '@mui/material';
import {
  DEAL_CONTENT_DESCRIPTION_LENGTH,
  DEAL_CONTENT_TITLE_LENGTH,
  DOCUMENT_FORMAT,
  PreIPODealStatus,
  SHORT_FIELD_MAX_LENGTH
} from '@constants/common';
import { translate } from '@utils/locale';
import {
  CustomSwitch,
  DualButton,
  RhfDropdown,
  RhfTextField
} from '@components';
import { PlusIcon, TrashIcon } from '@assets/icons';
import { colors } from '@constants/colors';
import { NewsUrlState } from '@modules/deals/create-deal/container/types';
import { NewsVerificationModal } from '@modules/deals/create-deal/container/NewsVerificationModal';
import FileUploader from '@containers/file-uploader/FileUploader';
import useScrollToTop from '@hooks/useScrollToTop';

import { PreIPODataRoomProps, PreIPODealFormValues } from '../types';
import { useGetPreIPOQuickLinkEntriesQuery } from '../api';
import { isPreIPOBottomActionDisabled } from '../common';
import FaqQuestions from '../components/FaqQuestions';

const PreIPODataRoom: FC<PreIPODataRoomProps> = ({
  errors,
  methods,
  handleSaveAsDraftClick,
  handleNextClick,
  isUpdateCreateDealLoading
}) => {
  const { getValues, setValue, clearErrors, control } = methods;

  const [newsState, setNewsState] = useState<NewsUrlState>({
    newsUrlValue: '',
    isNewsModalOpen: false,
    curNewsIndex: -1
  });

  const {
    fields: preIPODocumentFields,
    append: appendPreIPODocument,
    remove: removePreIPODocument
  } = useFieldArray({
    control,
    name: 'preIPODocuments'
  });

  const {
    fields: preIPOContentFields,
    append: appendPreIPOContent,
    remove: removePreIPOContent
  } = useFieldArray({
    control,
    name: 'preIPOContent'
  });

  const {
    fields: faqFields,
    append: appendFaq,
    remove: removeFaq
  } = useFieldArray({
    control,
    name: 'faq'
  });

  const {
    fields: newsFields,
    append: appendNews,
    remove: removeNews
  } = useFieldArray({
    control,
    name: 'news'
  });

  const {
    fields: quickLinksFields,
    append: appendQuickLinks,
    remove: removeQuickLinks
  } = useFieldArray({
    control,
    name: 'quickLinks'
  });

  const { data: quickLinkEntries } = useGetPreIPOQuickLinkEntriesQuery();

  const quickLinkEntriesDropDownList =
    quickLinkEntries?.quick_link_entry?.map(quickLinkEntry => ({
      id: quickLinkEntry.id,
      name: quickLinkEntry.name
    })) || [];

  const handleAddPreIPODocument = () => {
    appendPreIPODocument({
      id: '',
      title: '',
      description: '',
      document: { file: undefined, path: '' }
    });
  };

  const handleRemovePreIPODocument = (index: number) => {
    removePreIPODocument(index);
    const currentPreIPODocuments = getValues('preIPODocuments');
    if (currentPreIPODocuments.length === 0) {
      handleAddPreIPODocument();
    }
  };

  const handleAddPreIPOContent = () => {
    appendPreIPOContent({
      id: '',
      title: '',
      description: '',
      url: ''
    });
  };

  const handleRemovePreIPOContent = (index: number) => {
    removePreIPOContent(index);
    const currentPreIPOContent = getValues('preIPOContent');
    if (currentPreIPOContent.length === 0) {
      handleAddPreIPOContent();
    }
  };

  const handleAddFaq = () => {
    appendFaq({
      id: '',
      title: '',
      shouldDisplay: false,
      questions: [
        {
          id: '',
          question: '',
          answer: ''
        }
      ]
    });
  };

  const handleRemoveFaq = (index: number) => {
    removeFaq(index);
    const currentFaq = getValues('faq');
    if (currentFaq.length === 0) handleAddFaq();
  };

  const handleAddNews = () => {
    setNewsState({
      curNewsIndex: -1,
      newsUrlValue: '',
      isNewsModalOpen: true
    });
  };

  const handleAddQuickLinks = () => {
    appendQuickLinks({
      id: '',
      quickLinkEntryId: '',
      url: ''
    });
  };

  const handleRemoveQuickLink = (index: number) => {
    removeQuickLinks(index);
    const currentQuickLink = getValues('quickLinks');
    if (currentQuickLink.length === 0) handleAddQuickLinks();
  };

  const handleSwitchChange = (
    event: ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    setValue(
      name as never as keyof PreIPODealFormValues,
      event.target.value === 'enable',
      { shouldDirty: true }
    );
  };

  const handleNewsFieldClick = (
    e: React.MouseEvent<HTMLInputElement>,
    index: number
  ) => {
    const inputElement = e.target as HTMLInputElement;
    setNewsState({
      curNewsIndex: index,
      isNewsModalOpen: true,
      newsUrlValue: inputElement.value
    });
  };

  const handleAppendNews = (newsUrl: string) => {
    appendNews({
      id: '',
      url: newsUrl,
      order:
        newsFields.reduce(
          (max, obj) => (obj.order > max ? obj.order : max),
          0
        ) + 1
    });
  };

  const handleRemoveNews = (index: number) => {
    removeNews(index);
    const currentNews = getValues('news');
    if (currentNews.length === 0)
      appendNews({
        id: '',
        url: '',
        order: 0
      });
  };

  const handleNewsSetValue = (newsIndex: number, newsUrl: string) => {
    setValue(`news.${newsIndex}.url`, newsUrl, { shouldDirty: true });
  };

  const preIPODocumentUploadSuccessHandler = (
    file: File,
    path: string,
    index: number
  ) => {
    setValue(
      `preIPODocuments.${index}.document`,
      {
        file: file,
        path: path
      },
      { shouldDirty: true }
    );
    clearErrors(`preIPODocuments.${index}.document`);
  };

  const deleteFileHandler = (index: number) => {
    setValue(
      `preIPODocuments.${index}.document`,
      {
        file: undefined,
        path: ''
      },
      { shouldDirty: true }
    );
  };

  const updateNewsState = (newsStateValues: NewsUrlState) => {
    setNewsState(newsStateValues);
  };

  useScrollToTop();

  return (
    <div className="py-[24px] px-5">
      <FormProvider {...methods}>
        <form>
          <div className="flex flex-col">
            {/* Pre-IPO Documents */}
            <div className="flex flex-col border-b border-b-lightGrey">
              <div className="flex flex-col pb-10 mr-6 w-[67%] ">
                <div className="pb-6 text-xl font-semibold">
                  {translate(
                    'preIpoDeals.createPreIPODeals.dataRoom.preIPODocuments'
                  )}
                </div>
                <div className="flex flex-col gap-6 mb-6">
                  {preIPODocumentFields.map((field, index) => (
                    // eslint-disable-next-line tailwindcss/migration-from-tailwind-2
                    <div
                      key={field.id}
                      className="flex flex-col gap-1 p-4 bg-slate-50 bg-opacity-[0.5] rounded border border-lightGrey border-opacity-[0.5]">
                      <div className="flex flex-row justify-between items-center text-base font-medium text-black/80">
                        <div>
                          {translate(
                            'preIpoDeals.createPreIPODeals.dataRoom.preIPODocumentCount',
                            {
                              count: index + 1
                            }
                          )}
                        </div>
                        <TrashIcon
                          className="w-6 h-6 cursor-pointer trash-icon"
                          onClick={() => handleRemovePreIPODocument(index)}
                        />
                      </div>
                      <div className="flex flex-row justify-between mb-2 w-full align-middle  ">
                        <div className="pt-6 w-full">
                          <RhfTextField
                            name={`preIPODocuments.${index}.title`}
                            placeholder={translate(
                              'preIpoDeals.createPreIPODeals.dataRoom.documentName'
                            )}
                            control={control}
                            errors={{
                              [`preIPODocuments.${index}.title`]:
                                errors.preIPODocuments?.[index]?.title
                            }}
                            wrapperClass="w-[100%] "
                            onClickTextField={() =>
                              clearErrors(`preIPODocuments.${index}.title`)
                            }
                            maxLength={SHORT_FIELD_MAX_LENGTH}
                            showCharacterLimit
                            description={translate(
                              'preIpoDeals.createPreIPODeals.dataRoom.documentTitle'
                            )}
                          />
                        </div>
                      </div>
                      <div className="flex flex-row gap-6">
                        <RhfTextField
                          name={`preIPODocuments.${index}.description`}
                          placeholder={translate(
                            'preIpoDeals.createPreIPODeals.dataRoom.documentDescription'
                          )}
                          control={control}
                          errors={{
                            [`preIPODocuments.${index}.description`]:
                              errors.preIPODocuments?.[index]?.description
                          }}
                          multiline={true}
                          rows={1.9}
                          wrapperClass="mt-4 w-full"
                          onClickTextField={() => {
                            clearErrors(`preIPODocuments.${index}.description`);
                          }}
                          maxLength={SHORT_FIELD_MAX_LENGTH}
                          description={translate(
                            'preIpoDeals.createPreIPODeals.dataRoom.documentDescriptionInfo'
                          )}
                          showCharacterLimit
                        />

                        <div className="flex flex-row  ">
                          <div className=" text-base font-normal text-black/60 align-top">
                            {translate(
                              'preIpoDeals.createPreIPODeals.dataRoom.uploadDocument'
                            )}
                            <FileUploader
                              name={`preIPODocuments.${index}.document`}
                              enableCrop={true}
                              sizeInMb={3}
                              supportedFormats={DOCUMENT_FORMAT}
                              fileUploadSuccessHandler={(file, path) =>
                                preIPODocumentUploadSuccessHandler(
                                  file,
                                  path,
                                  index
                                )
                              }
                              deleteFileHandler={() => deleteFileHandler(index)}
                              errors={{
                                [`preIPODocuments.${index}.document`]:
                                  errors.preIPODocuments?.[index]?.document
                              }}
                              previousFile={
                                getValues(`preIPODocuments.${index}.document`)
                                  .file
                              }
                              showConfirmationModal={true}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mb-7">
                  <div className="group inline-block">
                    <Button
                      variant="outlined"
                      color="info"
                      size="small"
                      style={{
                        marginTop: 16
                      }}
                      className="group-hover:text-white group-hover:bg-brightBlue"
                      startIcon={<PlusIcon />}
                      onClick={handleAddPreIPODocument}>
                      {translate(
                        'preIpoDeals.createPreIPODeals.dataRoom.addAnotherDocument'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Content URL */}
            <div className="flex flex-col border-b border-b-lightGrey">
              <div className="flex flex-col py-10 mr-6 w-[67%]">
                <div className="pb-6 text-xl font-semibold">
                  {translate('preIpoDeals.createPreIPODeals.dataRoom.content')}
                </div>
                <div className="flex flex-col gap-6 mb-6">
                  {preIPOContentFields.map((field, index) => (
                    // eslint-disable-next-line tailwindcss/migration-from-tailwind-2
                    <div
                      key={field.id}
                      className="flex flex-col gap-1 p-4 bg-slate-50 bg-opacity-[0.5] rounded border border-lightGrey border-opacity-[0.5]">
                      <div className="flex flex-row justify-between items-center mb-6 text-base font-medium text-black/80">
                        <div>
                          {translate(
                            'preIpoDeals.createPreIPODeals.dataRoom.contentCount',
                            {
                              count: index + 1
                            }
                          )}
                        </div>
                        <TrashIcon
                          className="justify-end w-6 h-6 cursor-pointer trash-icon"
                          onClick={() => handleRemovePreIPOContent(index)}
                        />
                      </div>
                      <div className="flex flex-row gap-6">
                        <div className="flex-1 w-full">
                          <RhfTextField
                            name={`preIPOContent.${index}.title`}
                            placeholder={translate(
                              'preIpoDeals.createPreIPODeals.dataRoom.contentTitle'
                            )}
                            control={control}
                            errors={{
                              [`preIPOContent.${index}.title`]:
                                errors.preIPOContent?.[index]?.title
                            }}
                            wrapperClass="w-full "
                            onClickTextField={() => {
                              clearErrors(`preIPOContent.${index}.title`);
                            }}
                            showCharacterLimit
                            maxLength={DEAL_CONTENT_TITLE_LENGTH}
                          />
                        </div>
                        <div className="flex-1 w-full">
                          <RhfTextField
                            name={`preIPOContent.${index}.description`}
                            placeholder={translate(
                              'preIpoDeals.createPreIPODeals.dataRoom.contentDescription'
                            )}
                            control={control}
                            errors={{
                              [`preIPOContent.${index}.description`]:
                                errors.preIPOContent?.[index]?.description
                            }}
                            wrapperClass="w-full"
                            onClickTextField={() => {
                              clearErrors(`preIPOContent.${index}.description`);
                            }}
                            maxLength={DEAL_CONTENT_DESCRIPTION_LENGTH}
                            description={translate(
                              'preIpoDeals.createPreIPODeals.dataRoom.contentDescriptionInfo'
                            )}
                            showCharacterLimit
                          />
                        </div>
                      </div>
                      <RhfTextField
                        name={`preIPOContent.${index}.url`}
                        placeholder={translate(
                          'preIpoDeals.createPreIPODeals.dataRoom.contentUrl'
                        )}
                        control={control}
                        errors={{
                          [`preIPOContent.${index}.url`]:
                            errors.preIPOContent?.[index]?.url
                        }}
                        wrapperClass="w-full mt-4"
                        onClickTextField={() => {
                          clearErrors(`preIPOContent.${index}.url`);
                        }}
                        description={translate(
                          'preIpoDeals.createPreIPODeals.dataRoom.contentUrlDescription'
                        )}
                        showCharacterLimit
                      />
                    </div>
                  ))}
                </div>
                <div className="mb-7">
                  <div className="group inline-block">
                    <Button
                      variant="outlined"
                      color="info"
                      size="small"
                      style={{
                        marginTop: 16
                      }}
                      className="group-hover:text-white group-hover:bg-brightBlue"
                      startIcon={<PlusIcon />}
                      onClick={handleAddPreIPOContent}>
                      {translate(
                        'preIpoDeals.createPreIPODeals.dataRoom.addAnotherUrl'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ */}
            {false && (
              <div className="flex flex-col border-b border-b-lightGrey">
                <div className="flex flex-col py-10 mr-6 w-[67%] ">
                  <div className="pb-6 text-xl font-semibold">
                    {translate(
                      'preIpoDeals.createPreIPODeals.dataRoom.faq.title'
                    )}
                  </div>
                  <div className="flex flex-col gap-6 mb-6">
                    {faqFields.map((field, index) => (
                      // eslint-disable-next-line tailwindcss/migration-from-tailwind-2
                      <div
                        key={field.id}
                        className="flex flex-col gap-1 p-4 bg-slate-50 bg-opacity-[0.5] rounded border border-lightGrey border-opacity-[0.5]">
                        <div className="flex flex-row justify-between items-center text-base font-medium text-black/80">
                          <div>
                            {translate(
                              'preIpoDeals.createPreIPODeals.dataRoom.faq.faqCount',
                              {
                                count: index + 1
                              }
                            )}
                          </div>
                          <TrashIcon
                            className="w-6 h-6 cursor-pointer trash-icon"
                            onClick={() => handleRemoveFaq(index)}
                          />
                        </div>
                        <div className="flex flex-row gap-4  items-center mt-3">
                          <div className="text-base font-normal text-black/60">
                            {translate(
                              'preIpoDeals.createPreIPODeals.dataRoom.faq.faqVisibility'
                            )}
                          </div>
                          <CustomSwitch
                            control={control}
                            name={`faq.${index}.shouldDisplay`}
                            defaultValue={
                              getValues(`faq.${index}.shouldDisplay`) as boolean
                            }
                            handleChange={event =>
                              handleSwitchChange(
                                event,
                                `faq.${index}.shouldDisplay`
                              )
                            }
                          />
                        </div>
                        <div className="flex flex-col gap-6 w-full">
                          <RhfTextField
                            name={`faq.${index}.title`}
                            label={translate(
                              'preIpoDeals.createPreIPODeals.dataRoom.faq.questionTitle'
                            )}
                            placeholder={translate(
                              'deals.faq.questionTitlePlaceholder'
                            )}
                            control={control}
                            errors={{
                              [`faq.${index}.title`]: errors.faq?.[index]?.title
                            }}
                            wrapperClass="pt-3"
                            onClickTextField={() => {
                              clearErrors(`faq.${index}.title`);
                            }}
                            maxLength={SHORT_FIELD_MAX_LENGTH}
                            description={translate(
                              'preIpoDeals.createPreIPODeals.dataRoom.faq.questionTitlePlaceholder'
                            )}
                            showCharacterLimit
                          />
                          <FaqQuestions
                            control={control}
                            errors={errors}
                            clearErrors={clearErrors}
                            index={index}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="group inline-block">
                      <Button
                        variant="outlined"
                        color="info"
                        size="small"
                        style={{
                          marginTop: 16
                        }}
                        className=" group-hover:text-white group-hover:bg-brightBlue"
                        startIcon={<PlusIcon />}
                        onClick={handleAddFaq}>
                        {translate(
                          'preIpoDeals.createPreIPODeals.dataRoom.faq.addFaq'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* News */}
            <div className="flex flex-col border-b border-b-lightGrey">
              <div className="flex flex-col py-10 mr-6 w-[67%] ">
                <div className="pb-6 text-xl font-semibold">
                  {translate('preIpoDeals.createPreIPODeals.dataRoom.news')}
                </div>
                <div className="flex flex-col gap-6 mb-6">
                  {newsFields.map((field, index) => (
                    <div
                      key={field.id}
                      // eslint-disable-next-line tailwindcss/migration-from-tailwind-2
                      className="flex flex-col gap-1 p-4 bg-slate-50 bg-opacity-[0.5] rounded border border-lightGrey border-opacity-[0.5]">
                      <div className="flex flex-row justify-between mb-4 text-base font-medium text-black/80">
                        {translate(
                          'preIpoDeals.createPreIPODeals.dataRoom.newsCount',
                          {
                            count: index + 1
                          }
                        )}

                        <TrashIcon
                          className="justify-end w-6 h-6 cursor-pointer trash-icon"
                          onClick={() => handleRemoveNews(index)}
                        />
                      </div>
                      <RhfTextField
                        name={`news.${index}.url`}
                        placeholder={translate(
                          'preIpoDeals.createPreIPODeals.dataRoom.url'
                        )}
                        control={control}
                        errors={{
                          [`news.${index}.url`]: errors.news?.[index]?.url
                        }}
                        wrapperClass="mr-4 w-full"
                        onClickTextField={(
                          e: React.MouseEvent<HTMLInputElement, MouseEvent>
                        ) => handleNewsFieldClick(e, index)}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleNewsFieldClick(
                            e as unknown as React.MouseEvent<HTMLInputElement>,
                            index
                          )
                        }
                        showCharacterLimit
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <div className="group inline-block mb-7">
                    <Button
                      variant="outlined"
                      color="info"
                      size="small"
                      style={{
                        marginTop: 16
                      }}
                      className="group-hover:text-white group-hover:bg-brightBlue"
                      startIcon={<PlusIcon />}
                      onClick={handleAddNews}>
                      {translate(
                        'preIpoDeals.createPreIPODeals.dataRoom.addNews'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="flex flex-col">
              <div className="flex flex-col py-10 mr-6 w-[67%] ">
                <div className="pb-6 text-xl font-semibold">
                  {translate(
                    'preIpoDeals.createPreIPODeals.dataRoom.quickLinks'
                  )}
                </div>
                <div className="flex flex-col gap-6">
                  {quickLinksFields.map((field, index) => (
                    // eslint-disable-next-line tailwindcss/migration-from-tailwind-2
                    <div
                      key={field.id}
                      className="flex flex-col gap-1 p-4 bg-slate-50 bg-opacity-[0.5] rounded border border-lightGrey border-opacity-[0.5]">
                      <div className="flex flex-row justify-between items-center mb-4 text-base font-medium text-black/80">
                        <div>
                          {translate(
                            'preIpoDeals.createPreIPODeals.dataRoom.quickLinksCount',
                            {
                              count: index + 1
                            }
                          )}
                        </div>
                        <TrashIcon
                          className="justify-end w-6 h-6 cursor-pointer trash-icon"
                          onClick={() => handleRemoveQuickLink(index)}
                        />
                      </div>
                      <div className="flex flex-row">
                        <div className="flex w-[60%] ">
                          <RhfDropdown
                            name={`quickLinks.${index}.quickLinkEntryId`}
                            control={control}
                            placeholder={translate(
                              'preIpoDeals.createPreIPODeals.dataRoom.platform'
                            )}
                            dropdownLOV={quickLinkEntriesDropDownList}
                            selectedItem={getValues(
                              `quickLinks.${index}.quickLinkEntryId`
                            )}
                            errors={{
                              [`quickLinks.${index}.quickLinkEntryId`]:
                                errors.quickLinks?.[index]?.quickLinkEntryId
                            }}
                            wrapperClass="mr-6 w-full "
                          />
                        </div>
                        <div className="flex w-[87%] ">
                          <RhfTextField
                            name={`quickLinks.${index}.url`}
                            placeholder={translate(
                              'preIpoDeals.createPreIPODeals.dataRoom.quickLinkUrl'
                            )}
                            control={control}
                            errors={{
                              [`quickLinks.${index}.url`]:
                                errors.quickLinks?.[index]?.url
                            }}
                            wrapperClass="mr-4 w-full"
                            onClickTextField={() => {
                              clearErrors(`quickLinks.${index}.url`);
                            }}
                            showCharacterLimit
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="group inline-block mt-6 mb-7">
                    <Button
                      variant="outlined"
                      color="info"
                      size="small"
                      style={{
                        marginTop: 16
                      }}
                      className="group-hover:text-white group-hover:bg-brightBlue"
                      startIcon={<PlusIcon />}
                      onClick={handleAddQuickLinks}>
                      {translate(
                        'preIpoDeals.createPreIPODeals.dataRoom.addQuickLink'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end py-4">
            <DualButton
              isPrimaryDisabled={isPreIPOBottomActionDisabled(
                getValues('status') as PreIPODealStatus
              )}
              isSecondaryDisabled={isPreIPOBottomActionDisabled(
                getValues('status') as PreIPODealStatus
              )}
              primaryButtonLabel={translate('next')}
              secondaryButtonLabel={translate('saveAsDraft')}
              onChangePrimaryButton={handleNextClick}
              onChangeSecondaryButton={handleSaveAsDraftClick}
              isSecondaryButtonLoading={isUpdateCreateDealLoading}
              secondaryButtonCustomStyle={{
                backgroundColor: colors.gray88,
                color: colors.black,
                width: '136px'
              }}
            />
          </div>
        </form>
      </FormProvider>

      <NewsVerificationModal
        colors={colors}
        handleAppendNews={handleAppendNews}
        handleNewsSetValue={handleNewsSetValue}
        newsFields={newsFields}
        updateNewsState={updateNewsState}
        newsState={newsState}
      />
    </div>
  );
};

export default PreIPODataRoom;
